import { Box, makeStyles } from '@material-ui/core';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { TextFormField, TextFormFieldProps } from '@/components/shared/components/NewContextForms/TextFormField';
import { Icon } from '@/components/shared/components/Icon';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const JsonFormField = ({ name, ...props }: TextFormFieldProps<Record<string, string>>) => {
  const { setValue, watch } = useFormContext();

  const [justFormatted, setJustFormatted] = useState<boolean>(false);
  const [stringifiedValue, setStringifiedValue] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(true); // getFieldState needs an additional render to update after onChange

  const useStyles = makeStyles({
    root: {
      '& .MuiInputAdornment-root': {
        minHeight: '100%',
        bottom: '4px',
      },
    },
  });
  const classes = useStyles();

  const disableFormat = !isValid || watch(name) === stringifiedValue || stringifiedValue === '';

  const formatJson = () => {
    if (disableFormat) return;
    setValue(name, stringifiedValue, { shouldValidate: true });
    setJustFormatted(true);
    setTimeout(() => {
      setJustFormatted(false);
    }, 2000);
  };

  return (
    <TextFormField
      name={name}
      placeholder="{}"
      multiline
      minRows={4}
      maxRows={4}
      markAsOptional
      classes={classes}
      {...props}
      InputProps={{
        ...props.InputProps,
        endAdornment: (
          <JsonEndAdornment
            showCheck={justFormatted && isValid}
            onClick={formatJson}
            disabled={disableFormat || stringifiedValue === '{}'}
          />
        ),
      }}
      inputProps={{ spellCheck: false, ...props.inputProps }}
      style={{ fontFamily: 'monospace', fontSize: '0.87em', ...props.style }}
      onChangeCallback={(event) => {
        try {
          const obj = JSON.parse(event.target.value);
          setStringifiedValue(JSON.stringify(obj, undefined, 2));
          setIsValid(true);
        } catch {
          setIsValid(false);
        }
      }}
      onBlur={() => formatJson()}
    />
  );
};

const JsonEndAdornment = ({ showCheck, onClick, disabled }) => {
  const { palette } = useCustomTheme();

  return (
    <Box display={'flex'} alignItems={'flex-end'} height={'100%'}>
      <StandardButton onClick={onClick} disabled={!showCheck && disabled} color={'inherit'} size={'small'}>
        {showCheck ? (
          <Icon size={22} style={{ marginRight: '6px' }} color={palette.success.main} className={'appear-anim'}>
            {'check'}
          </Icon>
        ) : (
          <Icon size={22} style={{ marginRight: '6px' }}>
            {'auto_fix_high'}
          </Icon>
        )}
        <Text tId={'FORMAT_JSON'} />
      </StandardButton>
    </Box>
  );
};
